import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateIncident } from "@crm/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { lazy, Suspense } from "react";

// Lazy load the location picker
const LocationPicker = lazy(() => import("../../../components/LocationPicker"));

const ticketSchema = z.object({
  title: z.string().min(1, "Please describe your issue").max(255),
  description: z
    .string()
    .min(10, "Please provide more details about the issue"),
  category: z.enum([
    "hardware",
    "software",
    "network",
    "security",
    "access",
    "other",
  ]),
  priority: z.enum(["low", "medium", "high"]),
  location_address: z.string().optional(),
  location_latitude: z.number().optional(),
  location_longitude: z.number().optional(),
});

type TicketFormData = z.infer<typeof ticketSchema>;

const categoryOptions = [
  {
    value: "hardware",
    label: "Hardware Issue",
    description: "Computer, printer, phone, etc.",
  },
  {
    value: "software",
    label: "Software Issue",
    description: "Application not working, errors, etc.",
  },
  {
    value: "network",
    label: "Network Issue",
    description: "Internet, VPN, connectivity",
  },
  {
    value: "security",
    label: "Security Issue",
    description: "Suspicious activity, virus, breach",
  },
  {
    value: "access",
    label: "Access Request",
    description: "Need access to system or files",
  },
  {
    value: "other",
    label: "Other",
    description: "General questions or other issues",
  },
];

const priorityOptions = [
  { value: "low", label: "Low", description: "Not urgent, can wait" },
  {
    value: "medium",
    label: "Medium",
    description: "Important but not blocking work",
  },
  {
    value: "high",
    label: "High",
    description: "Blocking my work, need help soon",
  },
];

export function NewTicketPage() {
  const navigate = useNavigate();
  const createMutation = useCreateIncident();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TicketFormData>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      category: "other",
      priority: "medium",
    },
  });

  const locationData = watch([
    "location_latitude",
    "location_longitude",
    "location_address",
  ]);
  const currentLocation =
    locationData[0] != null && locationData[1] != null
      ? {
          latitude: locationData[0],
          longitude: locationData[1],
          address: locationData[2],
        }
      : null;

  const handleLocationChange = (
    location: { latitude: number; longitude: number; address?: string } | null
  ) => {
    if (location) {
      // Round to 6 decimal places to match backend DecimalField(max_digits=9, decimal_places=6)
      setValue(
        "location_latitude",
        Math.round(location.latitude * 1000000) / 1000000
      );
      setValue(
        "location_longitude",
        Math.round(location.longitude * 1000000) / 1000000
      );
      setValue("location_address", location.address || undefined);
    } else {
      setValue("location_latitude", undefined);
      setValue("location_longitude", undefined);
      setValue("location_address", undefined);
    }
  };

  const onSubmit = async (data: TicketFormData) => {
    try {
      // Ensure coordinates are properly rounded for backend validation
      const payload = {
        ...data,
        location_latitude: data.location_latitude
          ? Math.round(data.location_latitude * 1000000) / 1000000
          : undefined,
        location_longitude: data.location_longitude
          ? Math.round(data.location_longitude * 1000000) / 1000000
          : undefined,
      };
      const incident = await createMutation.mutateAsync(payload);
      toast.success("Your ticket has been submitted!");
      navigate(`/tickets/${incident.id}`);
    } catch (error: any) {
      console.error("Incident creation error:", error?.response?.data);
      const message =
        error?.response?.data?.error?.message || "Failed to submit ticket";
      const details = error?.response?.data?.error?.details;
      if (details) {
        console.error("Validation details:", details);
      }
      toast.error(message);
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tickets
        </Link>
        <h1 className="text-2xl font-semibold text-foreground">
          Submit a Support Ticket
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tell us what's wrong and we'll help you fix it.
        </p>
      </div>

      {/* Form */}
      <div className="rounded-lg border border-border bg-card p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label
              htmlFor="title"
              className="text-sm font-medium text-foreground"
            >
              What's the issue? <span className="text-destructive">*</span>
            </label>
            <input
              id="title"
              type="text"
              {...register("title")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              placeholder="e.g., My computer won't turn on"
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              What type of issue is this?
            </label>
            <div className="grid gap-2 md:grid-cols-2">
              {categoryOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-input bg-background p-3 hover:bg-muted/50"
                >
                  <input
                    type="radio"
                    value={option.value}
                    {...register("category")}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-foreground">
                      {option.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              How urgent is this?
            </label>
            <div className="flex gap-4">
              {priorityOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-input bg-background p-3 hover:bg-muted/50"
                >
                  <input
                    type="radio"
                    value={option.value}
                    {...register("priority")}
                  />
                  <div>
                    <p className="font-medium text-foreground">
                      {option.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label
              htmlFor="description"
              className="text-sm font-medium text-foreground"
            >
              Please describe the issue in detail{" "}
              <span className="text-destructive">*</span>
            </label>
            <textarea
              id="description"
              rows={5}
              {...register("description")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              placeholder="Include any error messages, when it started, and what you've already tried..."
            />
            {errors.description && (
              <p className="text-sm text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Location */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Your Location (optional)
            </label>
            <p className="text-xs text-muted-foreground">
              Share your location to help technicians find you faster
            </p>
            <Suspense
              fallback={
                <div className="h-16 flex items-center justify-center border border-dashed border-border rounded-lg">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              }
            >
              <LocationPicker
                value={currentLocation}
                onChange={handleLocationChange}
              />
            </Suspense>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link
              to="/"
              className="flex h-10 items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="flex h-10 items-center rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              {createMutation.isPending ? "Submitting..." : "Submit Ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
